import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  DisplayText,
  Modal,
  ButtonGroup,
  Page
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import {
  COMMON_CONSTANTS,
  getTranslatedText,
  reactSelectErrorStyles,
  RunbookNodeActivatorContent,
  withErrorBoundary
} from '@nile/components';
import { injectIntl, FormattedMessage } from 'react-intl';
import {
  DropdownIndicator,
  IndicatorSeparator
} from '@nile/components/src/Pebbles/select';
import Select from 'react-select';
import {
  fetchCollections,
  getParentHierarchy
} from '@nile/rmm/src/modules/knowledge-base/helper';
import { KB_CONSTANTS } from '@nile/rmm/src/modules/knowledge-base';
import { GET_KB_ITEM_META } from '@nile/rmm/src/modules/knowledge-base/graphql';
import { AccountClient } from '@nile/graphql';

const fetchKnowledgeBaseItems = async ({
  itemType,
  parentId = null,
  hasParent = false
}) => {
  let condition = [
    {
      attribute: 'itemType',
      operator: 'is',
      value: itemType
    }
  ];

  if (itemType === KB_CONSTANTS.KB_ARTICLE) {
    condition.push({
      attribute: 'status',
      operator: 'is',
      value: 'PUBLISHED'
    });
  }

  if (hasParent === false) {
    // For root collections (no parent)
    condition.push({
      attribute: 'parent',
      operator: 'isEmpty'
    });
  } else if (parentId) {
    // For items with a specific parent
    condition.push({
      attribute: 'parent.itemId',
      operator: 'is',
      value: `${parentId}`
    });
  }

  const res = await fetchCollections({
    listInfo: {
      sort: [
        {
          attribute: 'name',
          order: 'ASC'
        }
      ],
      condition: {
        joinOperator: 'AND',
        operands: condition
      }
    }
  });

  return (
    res?.collections?.map((el) => ({
      ...el,
      id: el?.itemId,
      label: el?.name,
      value: el?.itemId
    })) || []
  );
};

function KnowledgeBaseAction(props) {
  const { intl } = props;

  const saveLoading = false;
  const [errors, setErrors] = useState(null);
  const [collectionList, setCollectionList] = useState([]);
  const [collectionLoading, setCollectionLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  const [selectedArticle, setSelectedArticle] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedSubCollection, setSelectedSubCollection] = useState(null);

  // Separate state for articles and sub-collections
  const [articleList, setArticleList] = useState([]);
  const [articleListLoading, setArticleListLoading] = useState(false);
  const [subCollectionList, setSubCollectionList] = useState([]);
  const [subCollectionListLoading, setSubCollectionListLoading] = useState(
    false
  );

  const togglePopoverActive = useCallback(() => {
    setPopoverActive((active) => !active);
  }, []);

  const formData = props?.data?.formData || {};
  const parentActionId = props?.data?.formData?.parentActionId;
  const hasActionId = props?.data?.formData?.actionId;

  const [popoverActive, setPopoverActive] = useState(Boolean(!hasActionId));

  const activator = (
    <RunbookNodeActivatorContent
      nodeType="admin.runbooks.templateactions.referKnowledgebase"
      nodeName={formData?.name}
      iconName="KnowledgeBaseModule"
      callBackFunction={togglePopoverActive}
    />
  );
  const handleCollectionChange = (item) => {
    setSelectedCollection(item);
    setSelectedArticle(null);
    setSelectedSubCollection(null);
    // Clear dependent lists when parent collection changes
    setArticleList([]);
    setSubCollectionList([]);
  };

  const handleChangeArticle = (item) => {
    setSelectedArticle(item);
  };

  const handleChangeSubCollection = (item) => {
    setSelectedSubCollection(item);
    setSelectedArticle(null);
    setArticleList([]);
  };

  // Function to fetch articles based on selected collection or subcollection
  const handleFetchArticleItems = async (
    parentCollection,
    subCollection = null
  ) => {
    const targetCollection = subCollection || parentCollection;
    if (!targetCollection) return;

    setArticleListLoading(true);
    try {
      const articles = await fetchKnowledgeBaseItems({
        itemType: KB_CONSTANTS.KB_ARTICLE,
        parentId: targetCollection.itemId,
        hasParent: true
      });
      setArticleList(articles);
    } catch (error) {
      console.error('Error fetching articles:', error);
      setArticleList([]);
    } finally {
      setArticleListLoading(false);
    }
  };

  // Function to fetch sub-collections based on selected collection
  const handleFetchSubCollectionItems = async (parentCollection) => {
    if (!parentCollection) return;

    setSubCollectionListLoading(true);
    try {
      const subCollections = await fetchKnowledgeBaseItems({
        itemType: KB_CONSTANTS.KB_COLLECTION,
        parentId: parentCollection.itemId,
        hasParent: true
      });
      setSubCollectionList(subCollections);
    } catch (error) {
      console.error('Error fetching sub-collections:', error);
      setSubCollectionList([]);
    } finally {
      setSubCollectionListLoading(false);
    }
  };

  const handleFetchCollectionItems = async (parent) => {
    setCollectionLoading(true);
    try {
      const collections = await fetchKnowledgeBaseItems({
        itemType: KB_CONSTANTS.KB_COLLECTION,
        parentId: parent?.itemId || null,
        hasParent: parent?.itemId !== null && parent?.itemId !== undefined
      });
      setCollectionList(collections);
    } catch (error) {
      console.error('Error fetching collections:', error);
      setCollectionList([]);
    } finally {
      setCollectionLoading(false);
    }
  };

  const onSave = () => {
    const formDataTemp = {
      name: formData?.name || 'KNOWLEDGE_BASE', // name returned in response.
      id: props.id,
      actionType: 'SINGLE',
      actionProcessCategory: props?.isAutoAction ? 'ASYNC' : 'SYNC',
      actionValueType: 'KNOWLEDGE_BASE',
      actionValueData: {
        nodeId: props.id,
        kbArticle: {
          itemId: selectedArticle?.itemId,
          name: selectedArticle?.name
        }
      }
    };
    if (hasActionId) {
      formDataTemp.actionId = hasActionId;
    }
    if (parentActionId) {
      formDataTemp.parentActionId = parentActionId;
    }

    props.modifyNodeData(formDataTemp);
    togglePopoverActive();
  };

  const closeModal = () => {
    setPopoverActive(false);
    setSelectedArticle(null);
    setSelectedCollection(null);
    setSelectedSubCollection(null);
  };

  const handleNodeDelete = () => {
    props.data.handleNodeDelete(props.id, hasActionId);
    setPopoverActive(false);
  };

  const handleNodeCancel = useCallback(() => {
    closeModal();
    if (!hasActionId) {
      handleNodeDelete();
    }
  }, [hasActionId, handleNodeDelete]);

  const getArticle = (id) => {
    setDataLoading(true);
    AccountClient.query({
      query: GET_KB_ITEM_META,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
      variables: {
        input: {
          itemId: id
        }
      }
    })
      .then((res) => {
        if (!res?.data?.getKbItem) return;
        const parentColl = getParentHierarchy(res?.data?.getKbItem?.parent);
        setSelectedCollection({
          ...parentColl?.[0],
          label: parentColl?.[0]?.name,
          value: parentColl?.[0]?.itemId
        });
        setSelectedSubCollection(
          parentColl?.[1]
            ? {
                ...parentColl?.[1],
                label: parentColl?.[1]?.name,
                value: parentColl?.[1]?.itemId
              }
            : null
        );
        setSelectedArticle({
          ...res?.data?.getKbItem,
          label: res?.data?.getKbItem?.name,
          value: res?.data?.getKbItem?.itemId
        });
      })
      .catch((err) => {
        console.error('Error fetching article:', err);
      })
      .finally(() => {
        setDataLoading(false);
      });
  };

  useEffect(() => {
    if (formData?.actionValueData?.kbArticle?.itemId && popoverActive) {
      getArticle(formData?.actionValueData?.kbArticle?.itemId);
    }
  }, [formData?.actionValueData?.kbArticle?.itemId, popoverActive]);

  return (
    <Modal
      large
      activator={activator}
      open={popoverActive}
      onClose={closeModal}
      showCloseButton={false}
      noScroll
    >
      <Page
        title={getTranslatedText(
          intl,
          'admin.runbooks.templateactions.referKnowledgebase'
        )}
        contentHeight="110px"
        fullWidth
        variant="secondary"
        primaryAction={
          <ButtonGroup spacing="loose">
            {hasActionId && (
              <Button plainSecondary onClick={handleNodeDelete}>
                <div className="danger">
                  <FormattedMessage id="project.details.delete" />
                </div>
              </Button>
            )}
            <Button plainSecondary onClick={handleNodeCancel}>
              <FormattedMessage id="common.cancel" />
            </Button>
            <Button
              primary
              icon={<SVGIcon name="Save" />}
              onClick={onSave}
              loading={saveLoading}
              disabled={!selectedArticle || !selectedCollection}
            >
              <FormattedMessage id="common.save" />
            </Button>
          </ButtonGroup>
        }
      >
        <div className="p20 pl-40">
          <div className="flex center g15">
            <div className="w50 mb5">
              <DisplayText
                element="h6"
                isRequired
                size="large"
                weight="SemiBold"
              >
                <FormattedMessage id="common.app.kb.collection.label" />
              </DisplayText>

              <Select
                title={getTranslatedText(
                  intl,
                  'admin.employee.empInfo.desig.pl'
                )}
                value={selectedCollection}
                isLoading={collectionLoading || dataLoading}
                options={collectionList || []}
                isDisabled={dataLoading}
                onChange={(item) => handleCollectionChange(item)}
                components={{ IndicatorSeparator, DropdownIndicator }}
                onMenuOpen={() => handleFetchCollectionItems(null)}
                classNamePrefix="value_selector"
                menuPlacement="auto"
                menuPortalTarget={document.body}
                placeholder={getTranslatedText(
                  intl,
                  'app.kb.select.collection.label'
                )}
                styles={
                  errors?.script
                    ? {
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        menu: (base) => ({ ...base, zIndex: 9999 }),
                        ...reactSelectErrorStyles
                      }
                    : {
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        menu: (base) => ({ ...base, zIndex: 9999 })
                      }
                }
                isSearchable
              />
            </div>

            <div className="w50 mb5">
              <DisplayText element="h6" size="large" weight="SemiBold">
                <FormattedMessage id="app.kb.article.name" />
              </DisplayText>
              <Select
                title={getTranslatedText(
                  intl,
                  'admin.employee.empInfo.desig.pl'
                )}
                value={selectedArticle}
                isLoading={articleListLoading || dataLoading}
                options={articleList || []}
                isDisabled={!selectedCollection || dataLoading}
                onChange={(item) => handleChangeArticle(item)}
                components={{ IndicatorSeparator, DropdownIndicator }}
                onMenuOpen={() =>
                  handleFetchArticleItems(
                    selectedCollection,
                    selectedSubCollection
                  )
                }
                classNamePrefix="value_selector"
                menuPlacement="auto"
                menuPortalTarget={document.body}
                placeholder={getTranslatedText(
                  intl,
                  'app.kb.select.article.label'
                )}
                styles={
                  errors?.script
                    ? {
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        menu: (base) => ({ ...base, zIndex: 9999 }),
                        ...reactSelectErrorStyles
                      }
                    : {
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        menu: (base) => ({ ...base, zIndex: 9999 })
                      }
                }
                isSearchable
              />
            </div>
          </div>

          <div className="w50 mb5 mt-30">
            <DisplayText element="h6" size="large" weight="SemiBold">
              <FormattedMessage id="common.app.kb.sub.collection.label" />
            </DisplayText>
            <Select
              title={getTranslatedText(intl, 'admin.employee.empInfo.desig.pl')}
              value={selectedSubCollection}
              isLoading={subCollectionListLoading || dataLoading}
              options={subCollectionList || []}
              isDisabled={!selectedCollection || dataLoading}
              isClearable
              onChange={(item) => handleChangeSubCollection(item)}
              components={{ IndicatorSeparator, DropdownIndicator }}
              onMenuOpen={() =>
                handleFetchSubCollectionItems(selectedCollection)
              }
              classNamePrefix="value_selector"
              menuPlacement="auto"
              menuPortalTarget={document.body}
              placeholder={getTranslatedText(
                intl,
                'app.kb.select.sub.collection.label'
              )}
              styles={
                errors?.script
                  ? {
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({ ...base, zIndex: 9999 }),
                      ...reactSelectErrorStyles
                    }
                  : {
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({ ...base, zIndex: 9999 })
                    }
              }
              isSearchable
            />
          </div>
        </div>
      </Page>
    </Modal>
  );
}

export default withErrorBoundary(injectIntl(KnowledgeBaseAction));
